import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EntityFormSurface from '../EntityFormSurface';
import useExternalFormAddress from '../../../hooks/useExternalFormAddress';
import CloseButton from '../../atoms/CloseButton';
import Button from '../../atoms/Button';
import AddressAutocompleteInput from '../AddressAutocompleteInput';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { eventService } from '../../../services/eventService';
import { extractApiErrorMessage } from '../../../utils/apiErrors';
import { toAddressSelection } from '../../../utils/location';
import { SHAREABLE_OBJECT_VISIBILITY_OPTIONS } from '../PostVisibilityControl/options';
import {
    SCHEDULED_ENTITY_VISIBILITY_VALUE_SET,
    normalizeScheduledEntityFormVisibility,
    resolveScheduledEntityPayloadVisibility,
} from '../../../constants/scheduledEntityVisibility';
import '../EventCreateModal/index.scss';
import './index.scss';

const VISIBILITY_OPTIONS = SHAREABLE_OBJECT_VISIBILITY_OPTIONS.map((option) => option.value);
const NAME_MAX = 255;
const DESCRIPTION_MAX = 4000;
const WEBSITE_MAX = 512;
const PHONE_MAX = 64;
const AMENITY_MAX = 64;
const AMENITY_COUNT_MAX = 100;
const PHONE_PATTERN = /^[0-9+().\-\s]{3,64}$/;

function buildCreateVenueRequestIdempotencyKey() {
    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).slice(2, 12);
    return `venue-form-modal:create-venue:${timestamp}:${randomSuffix}`.slice(0, 128);
}

function readString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function toSelectedAddress(venue = {}) {
    return toAddressSelection(venue?.location);
}

function buildInitialFormState(venue = {}, { isEditMode = false } = {}) {
    const source = venue || {};
    const selectedAddress = toSelectedAddress(source);
    const hasExplicitVisibility = source.visibility !== undefined && source.visibility !== null && readString(source.visibility) !== '';
    return {
        name: readString(source.name || source.title),
        description: typeof source.description === 'string' ? source.description : '',
        addressDisplay: selectedAddress?.label || '',
        website: readString(source.website),
        phone: readString(source.phone),
        capacity: Number.isFinite(source.capacity) ? String(Math.max(0, Math.floor(source.capacity))) : '',
        amenities: Array.isArray(source.amenities) ? source.amenities.filter(Boolean).join(', ') : '',
        visibility: isEditMode || hasExplicitVisibility
            ? normalizeScheduledEntityFormVisibility(source.visibility)
            : 'PUBLIC',
    };
}

function parseAmenities(value) {
    return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

function isHttpUrl(value) {
    if (!value) {
        return true;
    }

    try {
        const url = new URL(value);
        return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
    } catch (_) {
        return false;
    }
}

export default function VenueFormModal({
    isOpen,
    embedded = false,
    mapAddress = undefined,
    onAddressSelected = undefined,
    locationPending = false,
    onBusyChange = undefined,
    mode = 'create',
    venue = null,
    onClose,
    onSaved = undefined,
    onDeleted = undefined,
}) {
    const { t } = useTranslation();
    const isEditMode = mode === 'edit';
    const [formState, setFormState] = useState(() => buildInitialFormState(venue, { isEditMode }));
    const [selectedAddress, setSelectedAddress] = useState(() => toSelectedAddress(venue));
    const [submitError, setSubmitError] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [hasEditedVisibility, setHasEditedVisibility] = useState(false);
    const mountedRef = useRef(true);
    const createRequestIdempotencyKeyRef = useRef(null);
    const createRequestPayloadFingerprintRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        createRequestIdempotencyKeyRef.current = null;
        createRequestPayloadFingerprintRef.current = null;
        setFormState(buildInitialFormState(venue, { isEditMode }));
        setSelectedAddress(toSelectedAddress(venue));
        setSubmitError(null);
        setValidationError(null);
        setIsSubmitting(false);
        setIsDeleteDialogOpen(false);
        setIsDeleting(false);
        setDeleteError(null);
        setHasEditedVisibility(false);
    }, [isEditMode, isOpen, venue]);

    useExternalFormAddress({
        isOpen,
        address: mapAddress,
        isResolving: locationPending,
        setSelectedAddress,
        setFormState,
        setValidationError,
    });

    const updateField = useCallback((field) => (event) => {
        const value = event?.target?.value ?? '';
        setFormState((current) => (
            current[field] === value ? current : { ...current, [field]: value }
        ));
        if (field === 'visibility') {
            setHasEditedVisibility(true);
        }
        setValidationError(null);
        setSubmitError(null);
    }, []);

    const handleAddressDisplayChange = useCallback((nextValue) => {
        setFormState((current) => (
            current.addressDisplay === nextValue ? current : { ...current, addressDisplay: nextValue }
        ));
        setSelectedAddress((current) => {
            if (!current) {
                return current;
            }
            return current.label === nextValue ? current : null;
        });
        setValidationError(null);
        setSubmitError(null);
    }, []);

    const handleAddressSelect = useCallback((geocoded) => {
        if (!geocoded) {
            setSelectedAddress(null);
            return;
        }
        setSelectedAddress(geocoded);
        onAddressSelected?.(geocoded);
        if (geocoded.label) {
            setFormState((current) => ({ ...current, addressDisplay: geocoded.label }));
        }
        setValidationError(null);
        setSubmitError(null);
    }, [onAddressSelected]);

    const validate = useCallback(() => {
        const name = formState.name.trim();
        if (!name) {
            return t('venuePage.form.errors.nameRequired');
        }
        if (name.length > NAME_MAX) {
            return t('venuePage.form.errors.nameTooLong', { max: NAME_MAX });
        }
        if (formState.description.length > DESCRIPTION_MAX) {
            return t('venuePage.form.errors.descriptionTooLong', { max: DESCRIPTION_MAX });
        }

        const addressLine = readString(selectedAddress?.addressLine);
        if (!selectedAddress
            || !addressLine
            || !readString(selectedAddress.cityName)
            || !readString(selectedAddress.countryName)
            || typeof selectedAddress.latitude !== 'number'
            || typeof selectedAddress.longitude !== 'number') {
            return t('venuePage.form.errors.addressRequired');
        }

        const website = formState.website.trim();
        if (website.length > WEBSITE_MAX) {
            return t('venuePage.form.errors.websiteTooLong', { max: WEBSITE_MAX });
        }
        if (!isHttpUrl(website)) {
            return t('venuePage.form.errors.websiteInvalid');
        }

        const phone = formState.phone.trim();
        if (phone.length > PHONE_MAX) {
            return t('venuePage.form.errors.phoneTooLong', { max: PHONE_MAX });
        }
        if (phone && !PHONE_PATTERN.test(phone)) {
            return t('venuePage.form.errors.phoneInvalid');
        }

        if (formState.capacity !== '') {
            if (!/^\d+$/.test(formState.capacity.trim())) {
                return t('venuePage.form.errors.capacityInvalid');
            }
        }

        const amenities = parseAmenities(formState.amenities);
        if (amenities.length > AMENITY_COUNT_MAX) {
            return t('venuePage.form.errors.amenitiesTooMany', { max: AMENITY_COUNT_MAX });
        }
        if (amenities.some((item) => item.length > AMENITY_MAX)) {
            return t('venuePage.form.errors.amenityTooLong', { max: AMENITY_MAX });
        }
        if (!SCHEDULED_ENTITY_VISIBILITY_VALUE_SET.has(formState.visibility)) {
            return t('venuePage.form.errors.visibilityInvalid', 'Choose Public, Friends, or Private.');
        }

        return null;
    }, [formState, selectedAddress, t]);

    const buildPayload = useCallback(() => {
        const countryCode = readString(selectedAddress?.countryCode).slice(0, 2).toUpperCase();
        const capacity = formState.capacity.trim();

        return {
            name: formState.name.trim(),
            description: formState.description.trim() || null,
            addressLine: readString(selectedAddress?.addressLine),
            city: readString(selectedAddress?.cityName),
            stateOrRegion: readString(selectedAddress?.regionName) || null,
            country: readString(selectedAddress?.countryName),
            countryCode: countryCode || null,
            latitude: selectedAddress.latitude,
            longitude: selectedAddress.longitude,
            website: formState.website.trim() || null,
            phone: formState.phone.trim() || null,
            capacity: capacity === '' ? null : Number(capacity),
            amenities: parseAmenities(formState.amenities),
            visibility: resolveScheduledEntityPayloadVisibility(
                formState.visibility,
                isEditMode ? venue?.visibility : null,
                hasEditedVisibility,
            ),
        };
    }, [formState, selectedAddress, isEditMode, venue, hasEditedVisibility]);

    const getCreateRequestIdempotencyKey = useCallback((payload) => {
        const payloadFingerprint = JSON.stringify(payload);

        if (!createRequestIdempotencyKeyRef.current
            || createRequestPayloadFingerprintRef.current !== payloadFingerprint) {
            createRequestIdempotencyKeyRef.current = buildCreateVenueRequestIdempotencyKey();
            createRequestPayloadFingerprintRef.current = payloadFingerprint;
        }
        return createRequestIdempotencyKeyRef.current;
    }, []);

    const handleSubmit = async (event) => {
        event?.preventDefault();
        if (isSubmitting || isDeleting || locationPending) {
            return;
        }

        const message = validate();
        if (message) {
            setValidationError(message);
            return;
        }

        setValidationError(null);
        setSubmitError(null);
        setIsSubmitting(true);

        try {
            const payload = buildPayload();
            const saved = isEditMode
                ? await eventService.updateVenue(venue?.id, payload)
                : await eventService.createVenue(payload, {
                    headers: {
                        'Idempotency-Key': getCreateRequestIdempotencyKey(payload),
                    },
                });
            if (!mountedRef.current) {
                return;
            }
            createRequestIdempotencyKeyRef.current = null;
            createRequestPayloadFingerprintRef.current = null;
            if (typeof onSaved === 'function') {
                onSaved(saved, { mode });
            }
            onClose?.();
        } catch (error) {
            if (!mountedRef.current) {
                return;
            }
            console.error(`Failed to ${isEditMode ? 'update' : 'create'} venue`, error);
            setSubmitError(extractApiErrorMessage(error, t(
                isEditMode ? 'venuePage.form.errors.update' : 'venuePage.form.errors.create',
            )));
        } finally {
            if (mountedRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    const handleClose = useCallback(() => {
        if (isSubmitting || isDeleting) {
            return;
        }
        onClose?.();
    }, [isSubmitting, isDeleting, onClose]);

    const handleOpenDeleteDialog = useCallback(() => {
        if (isSubmitting || isDeleting) {
            return;
        }
        setDeleteError(null);
        setIsDeleteDialogOpen(true);
    }, [isSubmitting, isDeleting]);

    const handleCancelDelete = useCallback(() => {
        if (isDeleting) {
            return;
        }
        setIsDeleteDialogOpen(false);
        setDeleteError(null);
    }, [isDeleting]);

    const handleConfirmDelete = useCallback(async () => {
        if (!isEditMode || !venue?.id || isDeleting) {
            return;
        }
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await eventService.deleteVenue(venue.id);
            if (!mountedRef.current) {
                return;
            }
            setIsDeleteDialogOpen(false);
            if (typeof onDeleted === 'function') {
                onDeleted(venue);
            }
            onClose?.();
        } catch (error) {
            if (!mountedRef.current) {
                return;
            }
            console.error('Failed to delete venue', error);
            setDeleteError(extractApiErrorMessage(error, t('venuePage.form.errors.delete')));
        } finally {
            if (mountedRef.current) {
                setIsDeleting(false);
            }
        }
    }, [isEditMode, venue, isDeleting, onDeleted, onClose, t]);

    if (!isOpen) {
        return null;
    }

    const translationRoot = isEditMode ? 'venuePage.editModal' : 'venuePage.createModal';
    const errorMessage = submitError || validationError;

    return (
        <EntityFormSurface
            embedded={embedded}
            busy={isSubmitting || isDeleting}
            onBusyChange={onBusyChange}
            onClose={handleClose}
            closeOnEsc={!isSubmitting && !isDeleteDialogOpen}
        >
            <form
                className={`event-create-modal venue-form-modal${embedded ? ' event-create-modal--embedded' : ''}`}
                role={embedded ? undefined : 'dialog'}
                aria-modal={embedded ? undefined : true}
                aria-labelledby="venue-form-modal-title"
                onSubmit={handleSubmit}
            >
                <header className="event-create-modal__hero venue-form-modal__hero">
                    <div className="event-create-modal__hero-overlay" aria-hidden="true" />
                    <div className="event-create-modal__hero-top">
                        <CloseButton
                            ariaLabel={t(`${translationRoot}.close`)}
                            onClick={handleClose}
                            className="event-create-modal__close"
                        />
                    </div>
                    <div className="event-create-modal__hero-bottom">
                        <span className="event-create-modal__chip venue-form-modal__chip">
                            {t(`${translationRoot}.eyebrow`)}
                        </span>
                        <h2 id="venue-form-modal-title" className="event-create-modal__title">
                            {t(`${translationRoot}.title`)}
                        </h2>
                        <p className="event-create-modal__subtitle">
                            {t(`${translationRoot}.subtitle`)}
                        </p>
                    </div>
                </header>

                <div className="event-create-modal__body">
                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('venuePage.form.sections.basics')}
                        </legend>
                        <div className="event-create-modal__field">
                            <label htmlFor="venue-form-name" className="event-create-modal__label">
                                {t('venuePage.form.fields.nameLabel')}
                                <span className="event-create-modal__required" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="venue-form-name"
                                type="text"
                                className="event-create-modal__input"
                                value={formState.name}
                                onChange={updateField('name')}
                                placeholder={t('venuePage.form.fields.namePlaceholder')}
                                maxLength={NAME_MAX}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        <div className="event-create-modal__field">
                            <label htmlFor="venue-form-description" className="event-create-modal__label">
                                {t('venuePage.form.fields.descriptionLabel')}
                            </label>
                            <textarea
                                id="venue-form-description"
                                className="event-create-modal__textarea"
                                value={formState.description}
                                onChange={updateField('description')}
                                placeholder={t('venuePage.form.fields.descriptionPlaceholder')}
                                maxLength={DESCRIPTION_MAX}
                                rows={4}
                                disabled={isSubmitting}
                            />
                        </div>
                    </fieldset>

                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('venuePage.form.sections.location')}
                        </legend>
                        <AddressAutocompleteInput
                            id="venue-form-address"
                            label={t('venuePage.form.fields.addressLabel')}
                            placeholder={t('venuePage.form.fields.addressPlaceholder')}
                            value={formState.addressDisplay}
                            onChange={handleAddressDisplayChange}
                            onSelect={handleAddressSelect}
                            countryCode="US"
                            disabled={isSubmitting || locationPending}
                            required
                            preventEnterWithoutSelection
                            helpText={selectedAddress
                                ? t('venuePage.form.fields.addressResolved', {
                                    city: selectedAddress.cityName || '',
                                    country: selectedAddress.countryName || '',
                                })
                                : t('venuePage.form.fields.addressHint')}
                            loadingText={t('venuePage.form.fields.addressLoading')}
                            emptyText={t('venuePage.form.fields.addressEmpty')}
                            className="event-create-modal__address"
                        />
                    </fieldset>

                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('venuePage.form.sections.contact')}
                        </legend>
                        <div className="event-create-modal__field">
                            <label htmlFor="venue-form-website" className="event-create-modal__label">
                                {t('venuePage.form.fields.websiteLabel')}
                            </label>
                            <input
                                id="venue-form-website"
                                type="url"
                                className="event-create-modal__input"
                                value={formState.website}
                                onChange={updateField('website')}
                                placeholder={t('venuePage.form.fields.websitePlaceholder')}
                                maxLength={WEBSITE_MAX}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="event-create-modal__row event-create-modal__row--two">
                            <div className="event-create-modal__field">
                                <label htmlFor="venue-form-phone" className="event-create-modal__label">
                                    {t('venuePage.form.fields.phoneLabel')}
                                </label>
                                <input
                                    id="venue-form-phone"
                                    type="tel"
                                    className="event-create-modal__input"
                                    value={formState.phone}
                                    onChange={updateField('phone')}
                                    placeholder={t('venuePage.form.fields.phonePlaceholder')}
                                    maxLength={PHONE_MAX}
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="event-create-modal__field">
                                <label htmlFor="venue-form-capacity" className="event-create-modal__label">
                                    {t('venuePage.form.fields.capacityLabel')}
                                </label>
                                <input
                                    id="venue-form-capacity"
                                    type="number"
                                    min="0"
                                    inputMode="numeric"
                                    className="event-create-modal__input"
                                    value={formState.capacity}
                                    onChange={updateField('capacity')}
                                    placeholder={t('venuePage.form.fields.capacityPlaceholder')}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="event-create-modal__field">
                            <label htmlFor="venue-form-amenities" className="event-create-modal__label">
                                {t('venuePage.form.fields.amenitiesLabel')}
                            </label>
                            <input
                                id="venue-form-amenities"
                                type="text"
                                className="event-create-modal__input"
                                value={formState.amenities}
                                onChange={updateField('amenities')}
                                placeholder={t('venuePage.form.fields.amenitiesPlaceholder')}
                                disabled={isSubmitting}
                            />
                            <p className="event-create-modal__hint">
                                {t('venuePage.form.fields.amenitiesHint')}
                            </p>
                        </div>
                    </fieldset>

                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('venuePage.form.sections.access')}
                        </legend>
                        <div className="event-create-modal__field">
                            <label htmlFor="venue-form-visibility" className="event-create-modal__label">
                                {t('venuePage.form.fields.visibilityLabel')}
                            </label>
                            <select
                                id="venue-form-visibility"
                                className="event-create-modal__select"
                                value={formState.visibility}
                                onChange={updateField('visibility')}
                                disabled={isSubmitting}
                            >
                                {VISIBILITY_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {t(`venuePage.form.visibility.${value.toLowerCase()}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </fieldset>

                    {errorMessage ? (
                        <div className="event-create-modal__error" role="alert">
                            <p>{errorMessage}</p>
                        </div>
                    ) : null}
                </div>

                <footer className="event-create-modal__actions event-create-modal__actions--with-delete">
                    {isEditMode ? (
                        <button
                            type="button"
                            className="event-create-modal__delete"
                            onClick={handleOpenDeleteDialog}
                            disabled={isSubmitting || isDeleting}
                        >
                            {t('venuePage.form.actions.delete')}
                        </button>
                    ) : null}
                    <div className="event-create-modal__actions-end">
                        <Button
                            type="button"
                            variant="secondary"
                            shape="pill"
                            size="medium"
                            onClick={handleClose}
                            disabled={isSubmitting || isDeleting}
                            className="event-create-modal__cancel"
                        >
                            {t('venuePage.form.actions.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="gradient"
                            shape="pill"
                            size="medium"
                            disabled={locationPending || isSubmitting || isDeleting}
                            className="event-create-modal__submit"
                        >
                            {isSubmitting
                                ? t('venuePage.form.actions.submitting')
                                : t(`${translationRoot}.submit`)}
                        </Button>
                    </div>
                </footer>
            </form>
            {isEditMode ? (
                <ConfirmDeleteDialog
                    isOpen={isDeleteDialogOpen}
                    title={t('venuePage.form.deleteDialog.title')}
                    description={t('venuePage.form.deleteDialog.description')}
                    targetName={readString(venue?.name || venue?.title) || null}
                    confirmLabel={t('venuePage.form.deleteDialog.confirm')}
                    cancelLabel={t('venuePage.form.deleteDialog.cancel')}
                    isSubmitting={isDeleting}
                    errorMessage={deleteError}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            ) : null}
        </EntityFormSurface>
    );
}

VenueFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    embedded: PropTypes.bool,
    mapAddress: PropTypes.object,
    onAddressSelected: PropTypes.func,
    locationPending: PropTypes.bool,
    onBusyChange: PropTypes.func,
    mode: PropTypes.oneOf(['create', 'edit']),
    venue: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        website: PropTypes.string,
        phone: PropTypes.string,
        capacity: PropTypes.number,
        amenities: PropTypes.arrayOf(PropTypes.string),
        visibility: PropTypes.string,
        location: PropTypes.shape({
            addressLine: PropTypes.string,
            city: PropTypes.string,
            stateOrRegion: PropTypes.string,
            country: PropTypes.string,
            countryCode: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
    }),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
    onDeleted: PropTypes.func,
};
